/**
 * coDisplay (IDI-556) — the ONE place a change order's status label, tone
 * and title are worked out. Every CO surface (builder header, detail,
 * Review & Send, the job's Change Orders tab) imports from here; none keeps
 * its own label table.
 *
 * Stored statuses are unchanged (draft, sent, accepted, approved, applied,
 * declined, withdrawn); this maps them to the four the user reads:
 *   draft, withdrawn, none        → Draft
 *   sent                          → Sent
 *   accepted, approved, applied   → Approved
 *   declined                      → Rejected
 * (Adrian 10-21 ruling 5.) StatusPill's shared labels — used by quotes and
 * invoices — are deliberately left alone.
 *
 * Tones use StatusPill's tone vocabulary and match its canonical tone map
 * (draft amber, sent dark, decided green, declined red).
 */

import { formatChangeOrderNumber } from '../../utils/displayId';

const norm = (s) => String(s || '').toLowerCase().trim();

const APPROVED = ['accepted', 'approved', 'applied'];

export function coStatusLabel(status) {
  const s = norm(status);
  if (s === 'sent') return 'Sent';
  if (APPROVED.includes(s)) return 'Approved';
  if (s === 'declined') return 'Rejected';
  return 'Draft';
}

export function coStatusTone(status) {
  const s = norm(status);
  if (s === 'sent') return 'dark';
  if (APPROVED.includes(s)) return 'success';
  if (s === 'declined') return 'danger';
  return 'warning';
}

/** True once Add to Job has merged the CO into the job (IDI-460). */
export function isCoAddedToJob(co) {
  return norm(co?.status) === 'applied';
}

/** Draft, an old withdrawn CO, or no status: edited and sent like a draft. */
export function isCoDraftLike(status) {
  const s = norm(status);
  return s === '' || s === 'draft' || s === 'withdrawn' || s === 'none';
}

/** True for an Approved CO whose lines have not been added to the job yet. */
export function isCoApprovedNotAdded(co) {
  return coStatusLabel(co?.status) === 'Approved' && !isCoAddedToJob(co);
}

const UUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-/i;

/**
 * The CO's stored number ("CO-000012"), or '' — never a raw id. Formatting
 * is the shared formatChangeOrderNumber (IDI-480 owns the format); its
 * raw-id / '—' fallback is guarded out here.
 */
export function coNumber(co) {
  if (!co) return '';
  let n = formatChangeOrderNumber(co);
  if ((!n || n === '—' || n === String(co.id ?? '')) && co.change_order_number) {
    n = String(co.change_order_number).trim();
  }
  if (!n || n === '—') return '';
  if (UUID_LIKE.test(n) || n === String(co.id ?? '')) return '';
  return n;
}

/** The CO's own name (name or title — never its description). */
export function coName(co) {
  return String(co?.name || co?.title || '').trim();
}

/**
 * "<name> · CO-000012"; no name → "Change Order CO-000012"; no number →
 * "<name>" or "Change Order". Never a raw id (Sarah L831-855).
 */
export function coTitle(co) {
  const name = coName(co);
  const num = coNumber(co);
  if (name && num) return `${name} · ${num}`;
  if (num) return `Change Order ${num}`;
  return name || 'Change Order';
}
